import { Switch, Select, MenuItem, FormControl, Button, Skeleton, Fade } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Card from '../../CaseManagement/components/Card';
import { useAdminMemberInfoFormOptions, ProfileStage } from '../hooks/useAdminMemberInfoFormOptions';
import { Homeless } from 'types';
import useUpdateAdminMemberInfo from '../hooks/useUpdateAdminMemberInfo';
import React from 'react';

const useStyles = makeStyles(() => ({
    section: {
        marginBottom: '0px'
    },
    whiteContainer: {
        backgroundColor: '#FFFFFF',
        boxShadow: '0px 8px 16px 4px rgba(0, 0, 0, 0.05)',
        borderRadius: '16px',
        padding: '32px',
        marginTop: '32px',
        position: 'relative'
    },
    overlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderRadius: '16px',
        zIndex: 10
    },
    overlayContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
        fontSize: '20px',
        fontWeight: 600,
        textAlign: 'center',
        padding: '0 32px'
    },
    overlayIcon: {
        fontSize: '32px',
        marginBottom: '8px'
    },
    successOverlay: {
        color: '#1FAD64'
    },
    errorOverlay: {
        color: '#E87F7F'
    },
    toggleContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        marginBottom: '32px'
    },
    dateContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        marginBottom: '32px'
    },
    selectContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        marginBottom: '32px'
    },
    verticalSelectContainer: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        marginBottom: '32px',
        '& > span': {
            marginBottom: '8px'
        }
    },
    label: {
        fontSize: '20px',
        fontFamily: 'Manrope',
        color: '#1F2152',
        fontWeight: 400
    },
    switch: {
        '& .MuiSwitch-colorPrimary.Mui-checked': {
            color: '#7378E8'
        },
        '& .MuiSwitch-track': {
            backgroundColor: '#787880'
        },
        '& .MuiSwitch-colorPrimary.Mui-checked + .MuiSwitch-track': {
            backgroundColor: '#7378E8'
        }
    },
    datePicker: {
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: '#E3E3FA',
            },
            '&:hover fieldset': {
                borderColor: '#7378E8',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#E3E3FA',
            },
        },
        '& .MuiInputBase-input': {
            color: '#1F2152',
            fontSize: '16px',
            fontFamily: 'Manrope',
        }
    },
    select: {
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: '#E3E3FA',
            },
            '&:hover fieldset': {
                borderColor: '#7378E8',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#E3E3FA',
            },
        },
        '& .MuiInputBase-input': {
            color: '#1F2152',
            fontSize: '16px',
            fontFamily: 'Manrope',
        },
        '& .MuiMenuItem-root': {
            '&.Mui-selected': {
                backgroundColor: '#F6F7FF !important',
                '&:hover': {
                    backgroundColor: '#F6F7FF'
                }
            },
            '&:hover': {
                backgroundColor: '#F6F7FF'
            }
        }
    },
    fullWidthSelect: {
        width: '100%',
        '& .MuiOutlinedInput-root': {
            width: '100%'
        }
    },
    buttonContainer: {
        display: 'flex',
        gap: '16px',
        marginTop: '64px',
        width: '100%',
        justifyContent: 'space-between'
    },
    button: {
        textTransform: 'none',
        fontFamily: 'Manrope',
    },
    saveButton: {
        fontSize: '20px',
        fontWeight: 600,
        padding: '12px 32px',
        backgroundColor: '#7378E8',
        color: '#FFFFFF',
        borderRadius: '100px',
        '&:hover': {
            backgroundColor: '#7378E850'
        },
        '&:disabled': {
            backgroundColor: '#E3E3FA',
            color: '#FFFFFF'
        }
    },
    discardButton: {
        fontSize: '18px',
        fontWeight: 600,
        backgroundColor: 'transparent',
        color: '#7378E8',
        '&:hover': {
            backgroundColor: 'transparent',
            color: '#7378E850'
        },
        '&:disabled': {
            backgroundColor: 'transparent',
            color: '#E3E3FA'
        }
    }
}));

type Props = {
  homeless: Homeless;
};

const MemberInfo = ({ homeless }: Props): JSX.Element => {
    const classes = useStyles();
    const { 
        deployments, 
        organizations,
        profileStageOptions, 
        isLoading: isOptionsLoading, 
        error: optionsError
    } = useAdminMemberInfoFormOptions(homeless);

    const {
        formData,
        updateField,
        submitChanges,
        reset,
        error,
        isLoading
    } = useUpdateAdminMemberInfo(homeless);

    const [showOverlay, setShowOverlay] = React.useState(false);
    const [overlayType, setOverlayType] = React.useState<'success' | 'error' | null>(null);

    // Track initial values for change detection
    const hasChanges = 
        formData.is_development !== homeless.is_development ||
        formData.membership_extension !== homeless.membership_extension ||
        formData.membership_end_date?.toISOString() !== (homeless.membership_end_date ? new Date(homeless.membership_end_date).toISOString() : null) ||
        formData.profile_stage !== homeless.profile_stage ||
        formData.deployment_id !== homeless.deployment_id ||
        formData.organization_id !== homeless.organization_id;

    const handleDevProfileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        updateField('is_development', event.target.checked);
    };

    const handleMemberExtensionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        updateField('membership_extension', event.target.checked);
    };

    const handleProfileStageChange = (event: any) => {
        updateField('profile_stage', event.target.value as ProfileStage);
    };

    const handleDeploymentChange = (event: any) => {
        updateField('deployment_id', event.target.value);
    };

    const handleOrganizationChange = (event: any) => {
        updateField('organization_id', event.target.value);
    };

    const handleSubmit = async () => {
        try {
            await submitChanges();
            setOverlayType('success');
            setShowOverlay(true);
            setTimeout(() => {
                setShowOverlay(false);
                setOverlayType(null);
            }, 3000);
        } catch (err) {
            setOverlayType('error');
            setShowOverlay(true);
            setTimeout(() => {
                setShowOverlay(false);
                setOverlayType(null);
            }, 5000);
        }
    };

    const renderOverlay = () => {
        if (!showOverlay || !overlayType) return null;

        return (
            <Fade in={showOverlay}>
                <div className={classes.overlay}>
                    <div className={`${classes.overlayContent} ${overlayType === 'success' ? classes.successOverlay : classes.errorOverlay}`}>
                        <i className={`${classes.overlayIcon} fas ${overlayType === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`} />
                        <span>{overlayType === 'success' ? 'Changes saved successfully' : `Error: ${error}`}</span>
                    </div>
                </div>
            </Fade>
        );
    };

    if (optionsError) {
        return (
            <div className={classes.section}>
                <Card info adminInfo name="Member Info" count={0} />
                <div className={classes.whiteContainer}>
                    <div style={{ color: '#d32f2f', textAlign: 'center' }}>
                        Error loading member info: {optionsError}
                    </div>
                </div>
            </div>
        );
    }

    if (isOptionsLoading || isLoading) {
        return (
            <div className={classes.section}>
                <Card info adminInfo name="Member Info" count={0} />
                <Skeleton 
                    variant="rounded" 
                    height={500} 
                    sx={{ 
                        mt: 4,
                        borderRadius: '16px'
                    }} 
                />
            </div>
        );
    }

    return (
        <div className={classes.section}>
            <Card
                info
                adminInfo
                name="Member Info"
                count={0}
            />
            <div className={classes.whiteContainer}>
                {renderOverlay()}
                <div className={classes.toggleContainer}>
                    <span className={classes.label}>Mark as Development/Test Profile?</span>
                    <Switch
                        checked={formData.is_development}
                        onChange={handleDevProfileChange}
                        className={classes.switch}
                    />
                </div>
                <div className={classes.toggleContainer}>
                    <span className={classes.label}>Approve for 6 month extension</span>
                    <Switch
                        checked={formData.membership_extension}
                        onChange={handleMemberExtensionChange}
                        className={classes.switch}
                    />
                </div>
                <div className={classes.dateContainer}>
                    <span className={classes.label}>Membership End Date</span>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            value={formData.membership_end_date}
                            onChange={(newValue) => updateField('membership_end_date', newValue)}
                            className={classes.datePicker}
                            format="MMMM-dd-yyyy"
                        />
                    </LocalizationProvider>
                </div>
                <div className={classes.selectContainer}>
                    <span className={classes.label}>Profile Stage</span>
                    <FormControl className={classes.select}>
                        <Select
                            value={formData.profile_stage}
                            onChange={handleProfileStageChange}
                            MenuProps={{
                                sx: {
                                    '& .MuiMenuItem-root': {
                                        '&.Mui-selected': {
                                            backgroundColor: '#F6F7FF !important'
                                        },
                                        '&:hover': {
                                            backgroundColor: '#F6F7FF'
                                        }
                                    }
                                }
                            }}
                        >
                            {profileStageOptions.map(({ value, label }) => (
                                <MenuItem key={value} value={value}>
                                    {label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>
                <div className={classes.verticalSelectContainer}>
                    <span className={classes.label}>Deployment</span>
                    <FormControl className={`${classes.select} ${classes.fullWidthSelect}`}>
                        <Select
                            value={formData.deployment_id}
                            onChange={handleDeploymentChange}
                            MenuProps={{
                                sx: {
                                    '& .MuiMenuItem-root': {
                                        '&.Mui-selected': {
                                            backgroundColor: '#F6F7FF !important'
                                        },
                                        '&:hover': {
                                            backgroundColor: '#F6F7FF'
                                        }
                                    }
                                }
                            }}
                        >
                            {deployments.map((dep) => (
                                <MenuItem key={dep.id} value={dep.id}>
                                    {dep.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>
                <div className={classes.verticalSelectContainer} style={{ marginBottom: 0 }}>
                    <span className={classes.label}>Organization</span>
                    <FormControl className={`${classes.select} ${classes.fullWidthSelect}`}>
                        <Select
                            value={formData.organization_id}
                            onChange={handleOrganizationChange}
                            MenuProps={{
                                sx: {
                                    '& .MuiMenuItem-root': {
                                        '&.Mui-selected': {
                                            backgroundColor: '#F6F7FF !important'
                                        },
                                        '&:hover': {
                                            backgroundColor: '#F6F7FF'
                                        }
                                    }
                                }
                            }}
                        >
                            {organizations.map((org) => (
                                <MenuItem key={org.id} value={org.id}>
                                    {org.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>
                <div className={classes.buttonContainer}>
                    <Button 
                        className={`${classes.button} ${classes.discardButton}`}
                        onClick={reset}
                        disabled={!hasChanges}
                    >
                        Discard Changes
                    </Button>
                    <Button 
                        className={`${classes.button} ${classes.saveButton}`}
                        onClick={handleSubmit}
                        disabled={!hasChanges}
                    >
                        Save Changes
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default MemberInfo; 